/**
 * The Ville de Montréal works truck, built procedurally. Owned by `vehicle`.
 *
 * DESIGN BRIEF: this is the shape the player stares at for the entire game, from behind, at
 * speed. It has to read INSTANTLY as a municipal works truck: boxy cab, asphalt hopper on the
 * back, amber beacons, safety-yellow-and-white livery, a repair lance on the tailgate, and a
 * silhouette wide enough to give the road a foreground speed reference (reference/REFERENCE.md
 * §4 — "a big car low in frame" is one of the four things that make Crazy Taxi feel fast).
 *
 * COLOUR IS NOT OURS. `look` owns the palette (N10) and publishes a full truck livery:
 * truck.paint (safety yellow), truck.paint.white, truck.chassis, truck.hotbox, truck.metal,
 * truck.glass, truck.tyre, truck.light{,.brake,.beacon}. Every part is assigned to one of
 * those keys and we take the SHARED material wherever we do not need to animate it, so the
 * weather/wetness drive that `look` runs on those materials reaches the truck for free.
 * Private clones (instanceOf) are used only where we must animate: the pulsing beacon, the
 * brake-light spike, and the livery decal atlas.
 *
 * BUDGET: everything static is merged per material into one BufferGeometry each, so the whole
 * truck is ~12 draw calls including six instanced wheels.
 */

import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { TUNING } from './tuning.js';

const D = TUNING.dims;
const LIV = TUNING.livery;

/* ────────────────────────────────────────────────────────── geometry helpers */

const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();
const _p = new THREE.Vector3();
const _s = new THREE.Vector3();

/** Convert to non-indexed and dispose the original, so merge inputs are always uniform. */
function nonIndexed(geo) {
  const out = geo.toNonIndexed();
  geo.dispose();
  return out;
}

/** Accumulates transformed geometry destined for one merged mesh. */
class PartList {
  constructor() { this.geos = []; }

  /**
   * @param {THREE.BufferGeometry} geo  consumed — do not reuse after adding
   * @param {object} t                  {x,y,z,rx,ry,rz,sx,sy,sz}
   */
  add(geo, t) {
    const n = geo.attributes.position.count;
    if (!geo.attributes.uv) {
      geo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(n * 2), 2));
    }
    if (t) {
      _p.set(t.x || 0, t.y || 0, t.z || 0);
      _e.set(t.rx || 0, t.ry || 0, t.rz || 0);
      _q.setFromEuler(_e);
      _s.set(t.sx == null ? 1 : t.sx, t.sy == null ? 1 : t.sy, t.sz == null ? 1 : t.sz);
      _m.compose(_p, _q, _s);
      geo.applyMatrix4(_m);
    }
    this.geos.push(geo.index ? nonIndexed(geo) : geo);
    return this;
  }

  merge(name, uvScale) {
    if (!this.geos.length) return null;
    const merged = mergeGeometries(this.geos, false);
    for (const g of this.geos) g.dispose();
    this.geos.length = 0;
    if (!merged) return null;
    if (uvScale) boxProjectUVs(merged, uvScale);
    merged.computeBoundingSphere();
    merged.name = name;
    return merged;
  }
}

/**
 * Re-project UVs from world position along the dominant normal axis. Box UVs run 0..1 per
 * face regardless of how big the face is, which smears `look`'s paint/metal textures across
 * a 3 m panel. Projecting at a fixed texel density instead keeps the grain consistent.
 */
function boxProjectUVs(geo, scale) {
  const pos = geo.attributes.position;
  const nor = geo.attributes.normal;
  const uv = geo.attributes.uv;
  for (let i = 0; i < pos.count; i++) {
    const nx = Math.abs(nor.getX(i)), ny = Math.abs(nor.getY(i)), nz = Math.abs(nor.getZ(i));
    const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
    let u, v;
    if (ny >= nx && ny >= nz) { u = x; v = z; }
    else if (nx >= nz) { u = z; v = y; }
    else { u = x; v = y; }
    uv.setXY(i, u * scale, v * scale);
  }
  uv.needsUpdate = true;
}

const box = (w, h, d) => new THREE.BoxGeometry(w, h, d);

/**
 * A tapered box: a 4-sided cylinder rotated 45° so its flats face the axes. Cheap way to get
 * the outward flare of a hopper body without authoring vertices by hand.
 */
function taper(wBottom, wTop, h, depth, openEnded = false) {
  const g = new THREE.CylinderGeometry(wTop * Math.SQRT1_2, wBottom * Math.SQRT1_2, h, 4, 1, openEnded);
  g.rotateY(Math.PI / 4);
  g.scale(1, 1, depth / wBottom);
  return g;
}

/** A wedge (triangular prism) — cab roof chamfer and the rear chute. */
function wedge(w, h, d) {
  const g = new THREE.BufferGeometry();
  const hw = w / 2, hh = h / 2, hd = d / 2;
  const v = [
    -hw, -hh, -hd, -hw, -hh, hd, -hw, hh, hd,
    hw, -hh, -hd, hw, hh, hd, hw, -hh, hd,
    -hw, -hh, -hd, hw, -hh, -hd, hw, -hh, hd, -hw, -hh, -hd, hw, -hh, hd, -hw, -hh, hd,
    -hw, -hh, hd, hw, -hh, hd, hw, hh, hd, -hw, -hh, hd, hw, hh, hd, -hw, hh, hd,
    -hw, -hh, -hd, -hw, hh, hd, hw, hh, hd, -hw, -hh, -hd, hw, hh, hd, hw, -hh, -hd,
  ];
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(v), 3));
  g.computeVertexNormals();
  return g;
}

/** Plane quad with an explicit UV window into the livery atlas. */
function decalQuad(w, h, u0, v0, u1, v1) {
  const g = new THREE.PlaneGeometry(w, h);
  const uv = g.attributes.uv;
  for (let i = 0; i < uv.count; i++) {
    const u = uv.getX(i), v = uv.getY(i);
    uv.setXY(i, u0 + (u1 - u0) * u, v0 + (v1 - v0) * v);
  }
  uv.needsUpdate = true;
  return g;
}

/* ────────────────────────────────────────────────────────── livery texture */

/**
 * One 512×512 canvas atlas holding three decal panels:
 *   canvas y 0-256   (v 0.5-1.0) — cab door: city crest roundel + VILLE DE MONTRÉAL
 *   canvas y 256-470 (v 0.08-0.5) — hopper flank: TRAVAUX PUBLICS + unit number
 *   canvas y 470-512 (v 0.0-0.08) — hazard chevron strip for the tailgate
 * Drawn entirely in code. No external assets (ARCHITECTURE.md N1).
 */
function buildLiveryTexture(rng) {
  const c = document.createElement('canvas');
  c.width = 512; c.height = 512;
  const g = c.getContext('2d');
  const hex = (v) => '#' + v.toString(16).padStart(6, '0');

  /**
   * ROUND 11, `night-rain` at 700 %: *"TRAVAUX and PUBLICS run together with zero word space —
   * the X crossbar physically intersects the P bowl."* Plus a second line rendered at contrast
   * so low it is nearly invisible, with its baseline running off the left edge of the panel.
   *
   * ROOT CAUSE, and it is structural rather than a typo: the two-tone wordmark was
   * `fillText('TRAVAUX', 14, …)` followed by `fillText('PUBLICS', 258, …)` — a HARDCODED
   * second x. `LIV` asks for `"Arial Narrow"` first, and N1 forbids loading a font, so which
   * face we actually get is whatever the machine happens to have. Headless Chromium has no
   * Arial Narrow and falls back to Helvetica/Arial, where bold 66 px "TRAVAUX" is ~290 px
   * wide — 32 px PAST the hardcoded start of "PUBLICS". The layout was correct for exactly one
   * font and broken for every fallback, which is also why two critics read two different unit
   * numbers off the same constant string: the text is not generated per-instance, it is
   * illegible in different ways at different sizes.
   *
   * So nothing here hardcodes an advance any more. `fit` shrinks a run until it fits the box it
   * is given, and `runs` measures each span and places the next one after it. Font-fallback
   * proof by construction.
   */
  const fit = (text, px, maxW, weight = 'bold') => {
    let size = px;
    for (let i = 0; i < 24; i++) {
      g.font = `${weight} ${size}px "Arial Narrow", "Helvetica Neue", Helvetica, Arial, sans-serif`;
      if (g.measureText(text).width <= maxW) break;
      size -= Math.max(1, Math.round(size * 0.06));
    }
    return size;
  };
  /** Draw coloured spans left to right, measuring each. Returns the total width drawn. */
  const runs = (spans, x, y, px, maxW, gap = 0) => {
    const whole = spans.map((s) => s[0]).join('');
    const size = fit(whole + (gap ? ' ' : ''), px, maxW - gap * (spans.length - 1));
    let cx = x;
    for (const [text, colour] of spans) {
      g.font = `bold ${size}px "Arial Narrow", "Helvetica Neue", Helvetica, Arial, sans-serif`;
      g.fillStyle = colour;
      g.fillText(text, cx, y);
      cx += g.measureText(text).width + gap;
    }
    return cx - x;
  };
  const line = (text, x, y, px, maxW, colour, weight = 'bold') => {
    const size = fit(text, px, maxW, weight);
    g.font = `${weight} ${size}px "Arial Narrow", "Helvetica Neue", Helvetica, Arial, sans-serif`;
    g.fillStyle = colour;
    g.fillText(text, x, y);
  };

  // ---- door panel
  g.fillStyle = hex(LIV.body);
  g.fillRect(0, 0, 512, 256);
  g.fillStyle = hex(LIV.white);
  g.fillRect(0, 190, 512, 66);
  drawCrest(g, 128, 112, 96);
  g.textBaseline = 'middle';
  line('VILLE DE', 230, 78, 46, 268, hex(LIV.ink));
  line('MONTRÉAL', 230, 130, 46, 268, hex(LIV.ink));
  line('SERVICE DES TRAVAUX PUBLICS', 16, 222, 30, 480, hex(LIV.ink));

  // ---- hopper flank panel
  g.fillStyle = hex(LIV.body);
  g.fillRect(0, 256, 512, 214);
  g.fillStyle = hex(LIV.white);
  g.fillRect(0, 292, 512, 92);
  // Two-tone wordmark, MEASURED not hardcoded — see `runs`. The gap is a real word space, so
  // "TRAVAUX PUBLICS" reads as two words at every fallback face.
  runs([['TRAVAUX', hex(LIV.ink)], ['PUBLICS', hex(LIV.accent)]], 14, 338, 66, 484, 16);
  /**
   * The unit number was drawn in `LIV.white` at y 424 — which is BELOW the white band
   * (292–384), i.e. white on safety yellow. That is the "contrast so low it is nearly
   * invisible", and it is why one critic read "UNITÉ 244" off the string "UNITÉ 4-2-0-4".
   * Ink on the yellow is both legible and what a works truck actually carries.
   */
  line('UNITÉ 4-2-0-4', 14, 424, 50, 484, hex(LIV.ink));

  // ---- hazard chevron strip
  g.save();
  g.beginPath(); g.rect(0, 470, 512, 42); g.clip();
  g.fillStyle = hex(LIV.ink);
  g.fillRect(0, 470, 512, 42);
  for (let i = -1; i < 14; i++) {
    if (i % 2) continue;
    g.fillStyle = hex(LIV.white);
    g.beginPath();
    const x = i * 40;
    g.moveTo(x, 512); g.lineTo(x + 26, 470); g.lineTo(x + 66, 470); g.lineTo(x + 40, 512);
    g.closePath(); g.fill();
  }
  g.restore();

  // Deterministic grime speckle so the panels are not vector-clean.
  g.globalAlpha = 0.10;
  g.fillStyle = '#2a2620';
  for (let i = 0; i < 420; i++) {
    const x = rng.next() * 512, y = rng.next() * 512, r = 1 + rng.next() * 5;
    g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill();
  }
  g.globalAlpha = 1;

  const tex = new THREE.CanvasTexture(c);
  tex.anisotropy = 4;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}

/** Simplified Montreal coat of arms: silver field, red cross, four quarter emblems. */
function drawCrest(g, cx, cy, size) {
  const hex = (v) => '#' + v.toString(16).padStart(6, '0');
  const r = size / 2;

  g.fillStyle = '#f2f1ec';
  g.beginPath();
  g.moveTo(cx - r, cy - r);
  g.lineTo(cx + r, cy - r);
  g.lineTo(cx + r, cy + r * 0.35);
  g.quadraticCurveTo(cx + r, cy + r, cx, cy + r * 1.2);
  g.quadraticCurveTo(cx - r, cy + r, cx - r, cy + r * 0.35);
  g.closePath();
  g.fill();
  g.lineWidth = 3; g.strokeStyle = hex(LIV.ink); g.stroke();

  g.fillStyle = hex(LIV.crestRed);
  g.fillRect(cx - r * 0.13, cy - r, r * 0.26, r * 2.1);
  g.fillRect(cx - r, cy - r * 0.13, r * 2, r * 0.26);

  const q = r * 0.5;
  // fleur-de-lis (top-left)
  g.fillStyle = hex(LIV.crestBlue);
  g.beginPath();
  g.moveTo(cx - q, cy - q - 13); g.lineTo(cx - q + 9, cy - q + 4); g.lineTo(cx - q - 9, cy - q + 4);
  g.closePath(); g.fill();
  g.fillRect(cx - q - 12, cy - q + 4, 24, 5);
  g.fillRect(cx - q - 2, cy - q + 6, 5, 11);
  // rose (top-right)
  g.fillStyle = hex(LIV.crestRed);
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2;
    g.beginPath(); g.arc(cx + q + Math.cos(a) * 7, cy - q + Math.sin(a) * 7, 5.5, 0, Math.PI * 2); g.fill();
  }
  // thistle (bottom-left)
  g.fillStyle = '#7c3f96';
  g.beginPath(); g.ellipse(cx - q, cy + q - 4, 7, 9, 0, 0, Math.PI * 2); g.fill();
  g.fillStyle = '#3f7a34';
  g.fillRect(cx - q - 2, cy + q + 3, 5, 12);
  // shamrock (bottom-right)
  g.fillStyle = '#2f8a3c';
  for (let i = 0; i < 3; i++) {
    const a = -Math.PI / 2 + (i / 3) * Math.PI * 2;
    g.beginPath(); g.arc(cx + q + Math.cos(a) * 6, cy + q + Math.sin(a) * 6, 6, 0, Math.PI * 2); g.fill();
  }
  g.fillStyle = '#2f8a3c';
  g.fillRect(cx + q - 2, cy + q + 6, 5, 10);
}

/* ────────────────────────────────────────────────────────── the truck */

export class TruckModel {
  /**
   * @param {object} ctx  game context (for ctx.materials)
   * @param {Rng} rng     the vehicle RNG fork — used only for texture grain
   */
  constructor(ctx, rng) {
    this.ctx = ctx;
    this.rng = rng;

    this.root = new THREE.Group();
    this.root.name = 'truck';

    /** Body group: everything that pitches/rolls with the chassis. */
    this.body = new THREE.Group();
    this.body.name = 'truck.body';
    this.root.add(this.body);

    this._materials = [];      // only the private clones we own and must dispose
    this._geometries = [];
    this._textures = [];

    this.wheelCount = 6;
    this.wheelMounts = [
      [D.trackHalf, D.wheelRadius, D.axleFrontZ],
      [-D.trackHalf, D.wheelRadius, D.axleFrontZ],
      [D.trackHalf, D.wheelRadius, D.axleRearZ],
      [-D.trackHalf, D.wheelRadius, D.axleRearZ],
      [D.trackHalf, D.wheelRadius, D.axleRear2Z],
      [-D.trackHalf, D.wheelRadius, D.axleRear2Z],
    ];
    /** Which physical contact (0..3 = L/R front, L/R rear) each visual wheel reads. */
    this.wheelContactIndex = [0, 1, 2, 3, 2, 3];

    this._wheelMatrix = new THREE.Matrix4();
    this._wheelPos = new THREE.Vector3();
    this._wheelQuat = new THREE.Quaternion();
    this._wheelEuler = new THREE.Euler();
    this._wheelScale = new THREE.Vector3(1, 1, 1);

    this._build();
  }

  // ------------------------------------------------------------------ materials

  /** First key `look` actually publishes wins. */
  _key(...candidates) {
    const M = this.ctx.materials;
    for (const k of candidates) {
      if (typeof M.has !== 'function' || M.has(k)) return k;
    }
    return candidates[candidates.length - 1];
  }

  /** Shared material — `look`'s weather drive reaches it for free. Never mutate. */
  _shared(...candidates) { return this.ctx.materials.get(this._key(...candidates)); }

  /** Private clone we are allowed to animate (ARCHITECTURE.md §4.4). */
  _own(key, tune) {
    const m = this.ctx.materials.instanceOf(this._key(key));
    m.name = 'vehicle:' + key;
    if (m.color) m.userData.vehicleBaseColor = m.color.clone();
    if (tune) tune(m);
    this._materials.push(m);
    return m;
  }

  /** Works for both the unlit MeshBasic lamps and any emissive standard material. */
  static setLampGain(mat, gain) {
    if (!mat) return;
    if (mat.emissive && mat.emissiveIntensity !== undefined) { mat.emissiveIntensity = gain; return; }
    const base = mat.userData && mat.userData.vehicleBaseColor;
    if (base && mat.color) mat.color.copy(base).multiplyScalar(gain);
  }

  _mesh(geo, mat, name, parent) {
    if (!geo) return null;
    const mesh = new THREE.Mesh(geo, mat);
    mesh.name = name;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    this._geometries.push(geo);
    (parent || this.body).add(mesh);
    return mesh;
  }

  // ------------------------------------------------------------------ build

  _build() {
    const P = {
      paint: new PartList(),     // safety yellow body
      white: new PartList(),     // livery bands
      chassis: new PartList(),   // dark frame / trim
      hotbox: new PartList(),    // the asphalt bin
      metal: new PartList(),     // steel fittings
      glass: new PartList(),
      lamp: new PartList(),      // headlights / markers
      tail: new PartList(),      // brake + tail cluster
      beacon: new PartList(),    // rear corner beacons (the roof bar is its own group)
      cone: new PartList(),      // decorative cones on the flank rack
      mix: new PartList(),       // the hot asphalt sitting in the bin
    };

    this._buildChassis(P);
    this._buildCab(P);
    this._buildHopper(P);
    this._buildRearGear(P);
    this._buildLights(P);

    this.beaconMaterial = this._own('truck.light.beacon', () => {});
    this.tailMaterial = this._own('truck.light.brake', () => {});

    this._mesh(P.paint.merge('truck.paint', 0.5), this._shared('truck.paint'), 'truck.paint');
    this._mesh(P.white.merge('truck.white', 0.5), this._shared('truck.paint.white', 'truck.paint'), 'truck.white');
    this._mesh(P.chassis.merge('truck.chassis', 0.6), this._shared('truck.chassis', 'truck.metal'), 'truck.chassis');
    this._mesh(P.hotbox.merge('truck.hotbox', 0.6), this._shared('truck.hotbox', 'truck.metal'), 'truck.hotbox');
    this._mesh(P.metal.merge('truck.metal', 0.8), this._shared('truck.metal'), 'truck.metal');
    this._mesh(P.glass.merge('truck.glass', 0.5), this._shared('truck.glass'), 'truck.glass');
    this._mesh(P.lamp.merge('truck.lamp', 1), this._shared('truck.light'), 'truck.lamp');
    this._mesh(P.tail.merge('truck.tail', 1), this.tailMaterial, 'truck.tail');
    this._mesh(P.beacon.merge('truck.rearBeacons', 1), this.beaconMaterial, 'truck.rearBeacons');
    this._mesh(P.cone.merge('truck.cones', 0.6), this._shared('prop.cone'), 'truck.cones');
    this.loadMesh = this._mesh(P.mix.merge('truck.mix', 0.7),
      this._shared('truck.hotbox', 'road.patch.fresh'), 'truck.mix');
    this.setLoad(TUNING.hopper.loadFillDefault);

    this._buildDecals();
    this._buildContactShadow();
    this._buildWheels();
    this._buildBeacon();
    this._buildNozzle();
  }

  /** Frame rails, bumpers, steps, fuel tank, exhaust — the dark stuff under the paint. */
  _buildChassis(P) {
    P.chassis.add(box(1.62, 0.30, 6.30), { y: 0.74, z: -0.05 });
    P.chassis.add(box(2.28, 0.20, 0.42), { y: 0.80, z: D.axleFrontZ });
    P.chassis.add(box(2.28, 0.20, 1.62), { y: 0.80, z: (D.axleRearZ + D.axleRear2Z) / 2 });

    // Front bumper + tow eyes
    P.chassis.add(box(2.52, 0.38, 0.34), { y: 0.76, z: 3.44 });
    P.metal.add(box(0.16, 0.16, 0.20), { x: 0.72, y: 0.76, z: 3.60 });
    P.metal.add(box(0.16, 0.16, 0.20), { x: -0.72, y: 0.76, z: 3.60 });
    /**
     * Rear bumper + step. ROUND 10/11: this step was `truck.metal` — `look`'s bright
     * galvanised key — and at 1.30 x 0.44 m it is not a fitting, it is a PANEL. Measured on
     * `combo-hud` (`tools/veh-toggle.mjs`, region 690,915,210,45): hiding `truck.metal` moved
     * the mean luma 104.8 → 147.1, i.e. this is the pale speckled slab a critic read as "a
     * grey lump with a black rectangular decal floating in the undercarriage shadow, attached
     * to no axle, chassis or wheel" — the black rectangle being the lower hazard chevron
     * decal 0.16 m above it. Dark frame steel, with a bright tread GRATING on top so the
     * accent stays where a fitting belongs.
     */
    P.chassis.add(box(2.46, 0.26, 0.30), { y: 0.72, z: -3.52 });
    P.chassis.add(box(1.30, 0.10, 0.44), { y: 0.58, z: -3.62 });
    for (let i = 0; i < 4; i++) {
      P.metal.add(box(1.22, 0.03, 0.05), { y: 0.635, z: -3.78 + i * 0.11 });
    }
    for (const sx of [1, -1]) P.chassis.add(box(0.07, 0.20, 0.09), { x: sx * 0.60, y: 0.66, z: -3.50 });

    /**
     * Mud flaps behind the rear tandem.
     *
     * ROUND 11, `winter-slush`: "the rear wheel's lowest point sits ABOVE the road surface."
     * Measured with `tools/veh-probe.mjs` it does NOT — all six tyres bite 10–13 mm INTO the
     * sampled ground at that shutter (gaps −0.0095 to −0.0160 m), so the geometry is right and
     * what fails is the READ: between the tyre and the road there is a hard tyre/snow edge with
     * no darkening, so the eye has nothing to weld the wheel down with. The contact blob that
     * would do it is `fx`'s (ours has been off since round 7 — see TUNING.contactShadow) and
     * is not ours to move.
     *
     * A mud flap is the part of the truck that can help from inside this directory: it hangs
     * to 0.28 m, closes the daylight behind the wheel, and casts into the same patch. Also the
     * single most "municipal works truck" detail still missing from the silhouette.
     */
    for (const sx of [1, -1]) {
      P.chassis.add(box(0.03, 0.68, 0.52), { x: sx * 1.03, y: 0.62, z: -3.24 });
      P.metal.add(box(0.05, 0.05, 0.54), { x: sx * 1.03, y: 0.94, z: -3.24 });   // hanger bar
    }

    // Fuel tank + battery box
    P.metal.add(new THREE.CylinderGeometry(0.30, 0.30, 1.10, 10), { x: 1.00, y: 0.92, z: 0.30, rz: Math.PI / 2 });
    P.chassis.add(box(0.46, 0.44, 0.80), { x: -1.02, y: 0.92, z: 0.20 });

    // Exhaust stack behind the cab
    P.metal.add(new THREE.CylinderGeometry(0.09, 0.10, 1.75, 8), { x: -1.16, y: 1.85, z: 0.62 });
    P.metal.add(new THREE.CylinderGeometry(0.12, 0.09, 0.16, 8), { x: -1.16, y: 2.78, z: 0.62 });

    // Mudflaps + side rails
    for (const sx of [1, -1]) {
      P.chassis.add(box(0.06, 0.44, 0.60), { x: sx * 1.06, y: 0.42, z: D.axleRear2Z - 0.68 });
      P.metal.add(box(0.10, 0.10, 2.30), { x: sx * 1.20, y: 0.62, z: -1.9 });
    }
  }

  /** Boxy cab: the front half of the silhouette. */
  _buildCab(P) {
    const cz = 2.06;
    P.paint.add(box(2.36, 1.06, 2.72), { y: 1.44, z: cz });
    P.paint.add(box(2.30, 1.00, 2.60), { y: 2.47, z: cz - 0.04 });
    P.paint.add(wedge(2.30, 0.26, 0.44), { y: 2.84, z: cz + 1.06, ry: Math.PI });
    P.chassis.add(box(2.26, 0.10, 2.48), { y: 3.00, z: cz - 0.04 });

    // White waist band — the livery separator that makes the truck read at distance
    P.white.add(box(2.40, 0.30, 2.74), { y: 1.08, z: cz });
    P.white.add(box(2.32, 0.62, 0.24), { y: 1.72, z: cz + 1.42 });
    P.chassis.add(box(1.86, 0.42, 0.14), { y: 1.72, z: cz + 1.52 });
    for (let i = 0; i < 4; i++) {
      P.metal.add(box(1.80, 0.05, 0.06), { y: 1.58 + i * 0.10, z: cz + 1.57 });
    }
    P.paint.add(box(2.30, 0.34, 0.20), { y: 1.20, z: cz + 1.44 });

    // Windscreen (raked) + side glass + mirrors
    P.glass.add(box(2.06, 0.94, 0.07), { y: 2.50, z: cz + 1.26, rx: -0.13 });
    for (const sx of [1, -1]) {
      P.glass.add(box(0.07, 0.74, 1.34), { x: sx * 1.16, y: 2.48, z: cz - 0.24 });
      P.metal.add(box(0.05, 0.05, 0.42), { x: sx * 1.30, y: 2.58, z: cz + 1.02, ry: sx * 0.32 });
      P.chassis.add(box(0.08, 0.52, 0.20), { x: sx * 1.48, y: 2.44, z: cz + 1.14 });
      P.metal.add(box(0.10, 0.06, 0.62), { x: sx * 1.20, y: 0.96, z: cz - 0.40 });
    }
    P.chassis.add(box(2.28, 0.94, 0.10), { y: 2.47, z: cz - 1.32 });

    // Roof marker pips
    for (let i = -1; i <= 1; i++) {
      P.lamp.add(box(0.16, 0.08, 0.12), { x: i * 0.52, y: 3.07, z: cz + 1.02 });
    }
  }

  /**
   * Asphalt hopper: tapered bin, an OPEN top rim, an interior shell, a heaped load, rear chute.
   *
   * Round 1 capped this with a single dark plate the size of the bed, which the critic read as
   * "an unfinished void". See TUNING.hopper for the full diagnosis. The bin is now built as a
   * real container: rails round an opening, walls you can see down into, and a load surface.
   */
  _buildHopper(P) {
    const HP = TUNING.hopper;
    const hz = HP.centreZ;
    // OPEN-ENDED. Capped, this cylinder put a solid yellow plate right across the top of the
    // bed at y 2.60, which is what actually read as "a near-black box" from the chase camera
    // — the load underneath it was never visible at all.
    P.paint.add(taper(2.24, 2.46, 1.56, 3.30, true), { y: 1.82, z: hz });
    P.white.add(taper(2.34, 2.40, 0.30, 3.36), { y: 1.34, z: hz });

    // ---- top RIM: four rails round the opening, not a lid
    const halfW = HP.outerW / 2, halfD = HP.outerD / 2, rw = HP.rimRailW;
    P.chassis.add(box(HP.outerW, HP.rimThick, rw), { y: HP.rimY, z: hz + halfD - rw / 2 });
    P.chassis.add(box(HP.outerW, HP.rimThick, rw), { y: HP.rimY, z: hz - halfD + rw / 2 });
    for (const sx of [1, -1]) {
      P.chassis.add(box(rw, HP.rimThick, HP.outerD - rw * 2),
        { x: sx * (halfW - rw / 2), y: HP.rimY, z: hz });
    }

    // ---- interior shell. Closes the cavity so the bin is never see-through, and gives the
    // opening four lit inner faces instead of one flat black hole.
    // Dark metal, NOT `truck.hotbox` — that key is authored as glowing hot mix and turned the
    // bin's inner faces bright orange, which is the load's job, not the bodywork's.
    const wallCY = HP.rimY - HP.wallH / 2;
    P.metal.add(box(HP.innerW, HP.wallH, HP.wallT), { y: wallCY, z: hz + HP.innerD / 2 });
    P.metal.add(box(HP.innerW, HP.wallH, HP.wallT), { y: wallCY, z: hz - HP.innerD / 2 });
    for (const sx of [1, -1]) {
      P.metal.add(box(HP.wallT, HP.wallH, HP.innerD), { x: sx * HP.innerW / 2, y: wallCY, z: hz });
    }
    P.metal.add(box(HP.innerW, 0.08, HP.innerD), { y: HP.floorY, z: hz });

    // ---- the load. Scaled every frame by how much of the round is repaired (see setLoad).
    // road.patch.fresh was tried for this first — it is glossy near-black and mirrors the sky,
    // so the bin read as full of water.
    this.loadBaseY = HP.floorY + 0.04;
    P.mix.add(this._buildLoadHeap(), { y: this.loadBaseY, z: hz });

    // Flank ribs
    for (let i = -1; i <= 1; i++) {
      for (const sx of [1, -1]) {
        P.chassis.add(box(0.06, 1.30, 0.12), { x: sx * 1.21, y: 1.86, z: hz + i * 1.05 });
      }
    }
    /**
     * Rear spreader chute.
     *
     * ROUND 10/11 — this and the rear step are the "unexplained concrete slabs" behind the
     * tailgate. Round 9 moved the chute off `truck.hotbox` (which made it a flat brown slab)
     * and onto `truck.metal`, which swapped one flat slab for a paler one: measured on
     * `combo-hud`, hiding `truck.metal` moves the region behind the tailgate 123.7 → 169.6
     * and 110.0 → 54.7, so these two parts ARE the pale speckled panels. `truck.metal` is
     * `look`'s bright galvanised-fitting key and a 1.70 m plate is not a fitting.
     *
     * Now dark frame steel with actual mechanism: two side cheeks, a hinge rod on chrome, and
     * an apron that hangs off it. Narrowed 1.70 → 1.52 so it sits INSIDE the tailgate frame
     * instead of running edge to edge like a wall.
     */
    P.chassis.add(wedge(1.52, 0.62, 0.72), { y: 1.32, z: hz - 1.90 });
    for (const sx of [1, -1]) {
      P.chassis.add(box(0.07, 0.46, 0.60), { x: sx * 0.79, y: 1.24, z: hz - 1.94 });
    }
    P.metal.add(new THREE.CylinderGeometry(0.045, 0.045, 1.44, 8),
      { y: 1.06, z: hz - 2.06, rz: Math.PI / 2 });
    P.chassis.add(box(1.36, 0.10, 0.46), { y: 1.02, z: hz - 2.16, rx: 0.32 });
    for (const sx of [1, -1]) {
      P.metal.add(box(0.06, 0.22, 0.06), { x: sx * 0.56, y: 1.16, z: hz - 2.02, rx: 0.32 });
    }

    // Tailgate frame + ladder
    P.metal.add(box(2.30, 0.10, 0.10), { y: 2.60, z: -3.30 });
    for (let i = 0; i < 3; i++) {
      P.metal.add(box(0.62, 0.05, 0.05), { x: -0.78, y: 1.24 + i * 0.46, z: -3.30 });
    }
    P.metal.add(box(0.05, 1.45, 0.05), { x: -1.06, y: 1.70, z: -3.30 });
    P.metal.add(box(0.05, 1.45, 0.05), { x: -0.50, y: 1.70, z: -3.30 });
  }

  /**
   * The heap of hot mix in the bin.
   *
   * ROUND 11, minor: "the load in the truck bed is a flat rectangle with generic blue-grey
   * noise on a grey plank with an unfinished cut edge — two primitives stacked, neither reads
   * as asphalt or gravel." It was `taper(2.10, 1.46, 1.0, 2.98)`: a four-sided prism whose
   * entire top is ONE quad, so the load surface could only ever be a flat rectangle.
   *
   * Now it is a heightfield: a grid whose interior vertices carry a seeded pseudo-random
   * offset on top of a domed profile, with the outer ring pinned to the bin floor so the heap
   * meets the walls instead of ending in a cut edge. Flat shading is deliberate — faceted
   * planes at ~0.1 m are what loose aggregate looks like at this range, and a smooth normal
   * would put it straight back to a slab.
   *
   * Authored 1 m tall with its pivot on the bin floor, exactly as `setLoad` expects. Uses the
   * subsystem's own `rng` fork, so it is deterministic (N3) and re-forked on reset (§7.2b).
   */
  _buildLoadHeap() {
    const HP = TUNING.hopper;
    const N = HP.heapGrid;
    const geo = new THREE.BufferGeometry();
    const cols = N + 1;
    const h = new Float32Array(cols * cols);
    for (let j = 0; j <= N; j++) {
      for (let i = 0; i <= N; i++) {
        const u = i / N, v = j / N;
        // Domed profile, zero on the rim: sin() reaches 0 at both edges by construction, so
        // the heap can never poke out through the bin walls or hang over a cut edge.
        const dome = Math.sin(u * Math.PI) * Math.sin(v * Math.PI);
        const edge = (i === 0 || j === 0 || i === N || j === N);
        h[j * cols + i] = edge ? 0 : dome * (HP.heapDome + this.rng.range(-HP.heapNoise, HP.heapNoise));
      }
    }
    const pos = [];
    const push = (i, j) => {
      pos.push(
        (i / N - 0.5) * HP.loadBaseW,
        h[j * cols + i],
        (j / N - 0.5) * HP.loadDepth,
      );
    };
    for (let j = 0; j < N; j++) {
      for (let i = 0; i < N; i++) {
        push(i, j); push(i, j + 1); push(i + 1, j);
        push(i + 1, j); push(i, j + 1); push(i + 1, j + 1);
      }
    }
    // Skirt: drop the rim to the bin floor so the heap is a closed volume seen from the side.
    const rim = [];
    for (let i = 0; i < N; i++) rim.push([i, 0, i + 1, 0], [i + 1, N, i, N]);
    for (let j = 0; j < N; j++) rim.push([N, j, N, j + 1], [0, j + 1, 0, j]);
    for (const [ax, az, bx, bz] of rim) {
      const px = (ax / N - 0.5) * HP.loadBaseW, pz = (az / N - 0.5) * HP.loadDepth;
      const qx = (bx / N - 0.5) * HP.loadBaseW, qz = (bz / N - 0.5) * HP.loadDepth;
      pos.push(px, 0, pz, qx, 0, qz, px, -HP.heapSkirt, pz);
      pos.push(qx, 0, qz, qx, -HP.heapSkirt, qz, px, -HP.heapSkirt, pz);
    }
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pos), 3));
    geo.computeVertexNormals();
    geo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array((pos.length / 3) * 2), 2));
    return geo;
  }

  /** Cones on the flank rack + shovel and broom: the details that say Montreal roadworks. */
  _buildRearGear(P) {
    for (let i = 0; i < 3; i++) {
      const z = -1.10 - i * 0.62;
      P.cone.add(new THREE.ConeGeometry(0.20, 0.52, 8, 1, true), { x: 1.30, y: 1.02, z, rz: -Math.PI / 2 });
      P.cone.add(box(0.05, 0.38, 0.38), { x: 1.05, y: 1.02, z });
    }
    P.metal.add(box(0.05, 0.05, 1.50), { x: -1.28, y: 1.10, z: -1.60, rx: 0.06 });
    P.metal.add(box(0.04, 0.26, 0.32), { x: -1.28, y: 1.08, z: -2.44 });
    P.chassis.add(box(0.05, 0.05, 1.40), { x: -1.28, y: 1.34, z: -1.70 });
    P.chassis.add(box(0.06, 0.16, 0.40), { x: -1.28, y: 1.30, z: -2.46 });
  }

  /**
   * Head, tail, reverse, marker lights, and the rear corner beacons.
   *
   * The rear cluster is its own material because the player stares at the back of this truck
   * for the entire game: the tails have to be the brightest thing on the silhouette and they
   * have to spike when you brake. The roof beacon is hidden behind the hopper from the chase
   * camera, so a pair of amber corner beacons goes on the tailgate header where you can
   * actually see them flash.
   */
  _buildLights(P) {
    const cz = 2.06;
    for (const sx of [1, -1]) {
      P.lamp.add(box(0.40, 0.26, 0.10), { x: sx * 0.86, y: 1.36, z: cz + 1.56 });
      P.chassis.add(box(0.48, 0.34, 0.06), { x: sx * 0.86, y: 1.36, z: cz + 1.52 });
      P.lamp.add(box(0.20, 0.14, 0.08), { x: sx * 1.10, y: 1.10, z: cz + 1.54 });
      /**
       * ROUND 9, `night-rain` gap 1: "front-style amber beacons, a red light bar, two WHITE
       * lamps and two red vertical lamps all crowd the same face, so a viewer cannot tell
       * whether the camera is behind the truck or in front of it."
       *
       * The white pair was the tell. `truck.light` is the HEADLAMP key — permanently lit
       * near-white — so putting it on the tailgate header put two headlights on the back of
       * the truck. Front-only emitters must not appear on the rear face.
       *
       * ROUND 11: the two loose amber markers that replaced them are gone as separate parts.
       * They are now the OUTER LENS MODULES of the rear light bar (`_buildLightBar`), at the
       * same height and on the same beacon material — same fix, one fewer floating box, and
       * the amber now reads as part of a fitting instead of two dots beside a red slab.
       */

      /**
       * Tail cluster. Round-2 critic, combo-hud: "the tail lamps are unlit saturated quads
       * with no housing, reading as glitch geometry against the black bodywork." They were
       * two bare lens boxes on a flat backing plate, sitting flush with the tailgate.
       *
       * Now the lenses are RECESSED: a backing plate behind them, and a chassis-material
       * frame standing 0.09 m proud all the way round, so the emissive reads as a light
       * sitting inside a fitting rather than a coloured rectangle stuck to the panel.
       */
      const tx = sx * 1.00, tz = -3.58;
      P.chassis.add(box(0.44, 1.10, 0.05), { x: tx, y: 0.98, z: -3.50 });   // backing plate
      P.tail.add(box(0.30, 0.62, 0.10), { x: tx, y: 1.16, z: tz });         // main lens
      P.tail.add(box(0.28, 0.18, 0.10), { x: tx, y: 0.80, z: tz });         // lower lens
      // Housing frame — four bars proud of the lens face.
      P.chassis.add(box(0.44, 0.07, 0.20), { x: tx, y: 1.53, z: tz - 0.03 });
      P.chassis.add(box(0.44, 0.07, 0.20), { x: tx, y: 0.68, z: tz - 0.03 });
      for (const fx2 of [1, -1]) {
        P.chassis.add(box(0.07, 0.92, 0.20), { x: tx + fx2 * 0.185, y: 1.10, z: tz - 0.03 });
      }
      // Divider between the brake lens and the indicator lens, so the pair reads as a unit.
      P.chassis.add(box(0.30, 0.05, 0.18), { x: tx, y: 0.93, z: tz - 0.02 });

      /**
       * Side markers. ROUND 11: these were the last two bare lens boxes on the truck, and the
       * rear one shows on the flank in `winter-slush` as a flat red blob with no fitting — the
       * same complaint as the light bar, one twentieth the size. Each now sits in a recessed
       * chassis cup with a bezel ring, so it reads as a lamp at 500 % and as a dot at 100 %.
       */
      for (const [zm, list] of [[0.9, P.lamp], [-3.0, P.tail]]) {
        P.chassis.add(box(0.05, 0.18, 0.26), { x: sx * 1.225, y: 1.24, z: zm });   // cup
        list.add(box(0.06, 0.10, 0.18), { x: sx * 1.24, y: 1.24, z: zm });         // lens
        for (const sy of [1, -1]) {
          P.chassis.add(box(0.055, 0.035, 0.26), { x: sx * 1.245, y: 1.24 + sy * 0.065, z: zm });
        }
      }

      // Rear corner beacons
      P.beacon.add(new THREE.CylinderGeometry(0.13, 0.15, 0.24, 8), { x: sx * 1.12, y: 2.80, z: -3.16 });
    }
    this._buildLightBar(P);
  }

  /**
   * Rear warning light bar across the tailgate header. See TUNING.lightBar for the round-11
   * fatal this replaces and for the bloom arithmetic that decides which modules are amber.
   *
   * Everything here lands in PartLists that already have a mesh, so the whole fitting costs
   * ZERO extra draw calls and zero materials: housing / bezel / dividers / rib on
   * `truck.chassis`, the cap on `truck.metal`, amber lenses on the beacon clone, red lenses
   * on the brake clone.
   */
  _buildLightBar(P) {
    const LB = TUNING.lightBar;
    const halfRun = (LB.lensCount - 1) / 2;

    // Housing. Dark, shaded, and deep enough that the bezel casts into it at a grazing angle.
    P.chassis.add(box(LB.width, LB.height, LB.depth), { y: LB.y, z: LB.housingZ });
    /**
     * Chrome cap. This is the answer to "ZERO tonal gradient across its entire 160 px span":
     * it is a SHADED `truck.metal` strip lying directly along the top of the lens run, so the
     * bar now contains a lit surface whose value falls off with the body's own normal, right
     * against the unlit lenses. A `MeshBasicMaterial` lens can never do that itself.
     */
    P.metal.add(box(LB.width + 0.10, 0.04, LB.depth + 0.02),
      { y: LB.y + LB.height / 2 + 0.015, z: LB.housingZ - 0.01 });

    // Bezel: top and bottom rails plus end caps, standing `bezelZ` proud of the lens plane so
    // the lenses are genuinely RECESSED rather than stuck on. The end caps also stop the bar
    // ending in mid-air, which is the other half of the critic's line.
    for (const sy of [1, -1]) {
      P.chassis.add(box(LB.width + 0.08, LB.bezelT, LB.bezelD),
        { y: LB.y + sy * (LB.lensH / 2 + LB.bezelT / 2), z: LB.bezelZ });
    }
    for (const sx of [1, -1]) {
      P.chassis.add(box(0.06, LB.height, LB.bezelD + 0.02),
        { x: sx * (LB.width / 2 + 0.01), y: LB.y, z: LB.bezelZ });
    }
    // Horizontal centre rib — splits every module into two lit cells, so at 700 % the bar has
    // 14 cells of structure instead of one span of flat colour.
    P.chassis.add(box(LB.width + 0.06, LB.ribH, LB.bezelD), { y: LB.y, z: LB.bezelZ });

    for (let i = 0; i < LB.lensCount; i++) {
      const x = (i - halfRun) * LB.lensPitch;
      // Amber at the ends: red carries only 0.2126 of the luma weight and physically cannot
      // reach `post.bloomThreshold`; amber does, at the gain the roof beacon already runs.
      const list = LB.amber.includes(i) ? P.beacon : P.tail;
      list.add(box(LB.lensW, LB.lensH, LB.lensD), { x, y: LB.y, z: LB.lensZ });
      if (i < LB.lensCount - 1) {
        P.chassis.add(box(LB.dividerW, LB.lensH + 0.02, LB.bezelD),
          { x: x + LB.lensPitch / 2, y: LB.y, z: LB.bezelZ });
      }
    }
  }

  /** Livery decal quads mapped into the single canvas atlas. */
  _buildDecals() {
    const tex = buildLiveryTexture(this.rng);
    this._textures.push(tex);

    let key = null;
    for (const k of ['truck.paint', 'sign.panel', 'road.markings']) {
      const probe = this.ctx.materials.get(this._key(k));
      if (probe && 'map' in probe) { key = k; break; }
    }
    if (!key) { tex.dispose(); this._textures.pop(); return; }

    const mat = this._own(key, (m) => {
      m.map = tex;
      if (m.color) m.color.set(0xffffff);
      if (m.roughness !== undefined) m.roughness = 0.5;
      if (m.metalness !== undefined) m.metalness = 0.0;
      m.polygonOffset = true;
      m.polygonOffsetFactor = -2;
      m.polygonOffsetUnits = -2;
      m.needsUpdate = true;
    });
    this.decalMaterial = mat;

    const parts = new PartList();
    const cz = 2.06;
    // u runs 0→1 across the quad's local −X→+X on BOTH flanks and on the tailgate: the
    // rotations work out so local +X is always screen-right to a viewer facing the panel.
    for (const sx of [1, -1]) {
      // Cab doors — atlas top half
      parts.add(decalQuad(1.30, 0.62, 0, 0.52, 1, 0.98),
        { x: sx * 1.195, y: 1.60, z: cz - 0.18, ry: sx * Math.PI / 2 });
      // Hopper flanks — atlas middle band
      parts.add(decalQuad(2.40, 1.04, 0, 0.10, 1, 0.48),
        { x: sx * 1.235, y: 1.92, z: -1.55, ry: sx * Math.PI / 2 });
    }
    // TAILGATE — the most-looked-at surface in the game. Hazard chevrons for contrast plus
    // the city identity panel, so the truck says MONTREAL from the angle you always see it.
    // The hopper's rear face LEANS BACK with the taper, so each panel sits at the z the
    // face actually occupies at that height (a flat −3.215 buried them inside the bin).
    parts.add(decalQuad(2.10, 0.40, 0, 0.004, 1, 0.078), { y: 1.70, z: -3.285, ry: Math.PI });
    parts.add(decalQuad(2.34, 0.24, 0, 0.004, 1, 0.078), { y: 0.74, z: -3.685, ry: Math.PI });
    parts.add(decalQuad(2.05, 0.60, 0, 0.535, 1, 0.965), { y: 2.22, z: -3.345, ry: Math.PI });

    this.decalMesh = this._mesh(parts.merge('truck.decal'), mat, 'truck.decal');
    if (this.decalMesh) this.decalMesh.castShadow = false;
  }

  /**
   * Contact shadow / ambient occlusion under the truck. See TUNING.contactShadow.
   *
   * Lives on `root`, not `body`: it must yaw with the truck but must NOT pitch, roll or heave
   * with the suspension, or the shadow lifts off the road every time you brake.
   */
  _buildContactShadow() {
    const CS = TUNING.contactShadow;
    // ROUND 7: off by default — `fx` auto-drives the hero blob from ctx.player with a
    // hard-edged sprite, and ours compounded with it into literal black. See TUNING.
    if (!CS.enabled) { this.shadowMesh = null; return; }
    const parts = new PartList();

    // STACKED IDENTICAL body quads. All of them live in one merged geometry drawn in
    // submission order with depthWrite off, so each blends over the last and the alpha
    // compounds as 1 - (1-a)^N. Identical sizes rather than concentric ones is the point:
    // concentric rings still leave the OUTER fringe — the only part the camera can see —
    // covered by a single nearly-transparent layer.
    for (let i = 0; i < CS.bodyLayers; i++) {
      const g = new THREE.PlaneGeometry(CS.bodyW, CS.bodyD);
      g.rotateX(-Math.PI / 2);
      parts.add(g, { y: CS.bodyLift + i * CS.layerStep, z: CS.bodyZ });
    }

    // A pool at each of the six tyre patches, stacked the same way and wide enough to spill
    // outboard of the tyre wall. On top of the body layers this takes the contact patch to
    // effectively opaque — the near-black #222823 REFERENCE §3.4 asks for.
    const tyreW = D.wheelWidth * CS.tyreScale;
    for (const m of this.wheelMounts) {
      for (let i = 0; i < CS.tyreLayers; i++) {
        const g = new THREE.PlaneGeometry(tyreW, tyreW * CS.tyreDepthScale);
        g.rotateX(-Math.PI / 2);
        parts.add(g, { x: m[0], y: CS.tyreLift + i * CS.layerStep, z: m[2] });
      }
    }

    // `fx.decal` is `look`'s ground decal: map RGB #1A1817, polygon-offset, depthWrite off,
    // authored by look (N10). Taken as a cached OPACITY VARIANT — `get(key, opts)` variants
    // share the base material's shader program, so this costs zero of our 49/45 programs and
    // gives us the one strength knob the geometry cannot supply.
    const mat = this.ctx.materials.get(this._key('fx.decal', 'fx.tyremark'), { opacity: CS.opacity });
    this.shadowMesh = this._mesh(parts.merge('truck.contactShadow'), mat, 'truck.contactShadow', this.root);
    if (this.shadowMesh) {
      this.shadowMesh.castShadow = false;
      this.shadowMesh.receiveShadow = false;
      this.shadowMesh.renderOrder = 1;
    }
  }

  /**
   * Fade the contact shadow out as the truck leaves the ground, so a jump reads as airborne.
   * `height` is metres of the wheel plane above the ground.
   */
  setContactShadow(height) {
    if (!this.shadowMesh) return;
    const CS = TUNING.contactShadow;
    let k = 1 - (height / CS.airFadeHeight);
    if (k < CS.minOpacityScale) k = CS.minOpacityScale;
    else if (k > 1) k = 1;
    this.shadowMesh.scale.set(k, 1, k);
    this.shadowMesh.visible = height < CS.airFadeHeight * 1.5;
  }

  /** Six wheels as two instanced meshes: tyres and rims. 2 draw calls total. */
  _buildWheels() {
    const r = D.wheelRadius, w = D.wheelWidth;
    const WF = TUNING.wheelFace;
    /**
     * ROUND 7 — the tread band is OPEN-ENDED and the sidewall is an annulus, so the rim can
     * sit INSIDE the tyre's width and still be seen. It used to be a capped cylinder, which
     * is why the round-4 rim had to stand proud of the sidewall to be visible at all — and
     * standing proud is exactly what put it in front of fx's blur disc (see TUNING.wheelFace).
     * A real tyre is a torus with a hole in it; building it that way lets the wheel face be
     * both legible and blurrable.
     */
    /**
     * ROUND 11 — "no tread, no sidewall lettering". The tread is now FOUR CIRCUMFERENTIAL
     * RIBS separated by three grooves, which is what a real truck rib tyre carries, and it is
     * chosen for a specific reason as well as for looking right:
     *
     * fx's SPIN sprite paints r = 0.26…0.86 of a disc of radius `0.55 × discScale 1.06` solid,
     * i.e. 0.152…0.501 m — so the tread band at r ≈ 0.55 lives OUTSIDE the blur annulus and
     * whatever is authored there stays sharp at 94 km/h. Angular tread blocks would therefore
     * be a rotation cue in the one band the blur cannot cover, which is exactly the round-7/9
     * defect ("individually legible hub spokes at 87 km/h") in a new place. Circumferential
     * ribs are ROTATIONALLY INVARIANT: they look identical at every spin angle, so they can
     * never read as parked, and they still give the shoulder a grooved silhouette at rest.
     */
    const T = TUNING.tyre;
    const tyreParts = [];
    // Groove floor: ONE open cylinder under the whole tread band, so the grooves have a
    // visible bottom without paying for a floor per groove.
    const floor = new THREE.CylinderGeometry(r - T.grooveDepth, r - T.grooveDepth, w, 14, 1, true);
    floor.rotateZ(Math.PI / 2);
    tyreParts.push(nonIndexed(floor));
    // Ribs: CLOSED cylinders, so each one has real end faces and the groove reads as a slot
    // cut into the tread rather than as a change of shade.
    const cw = w / T.ribCount;
    for (let i = 0; i < T.ribCount; i++) {
      const rib = new THREE.CylinderGeometry(r, r, cw * T.ribWidth, 18, 1, false);
      rib.rotateZ(Math.PI / 2);
      rib.translate(-w / 2 + cw * (i + 0.5), 0, 0);
      tyreParts.push(nonIndexed(rib));
    }
    const shoulder = new THREE.TorusGeometry(r * 0.94, 0.05, 4, 18);
    shoulder.rotateY(Math.PI / 2);
    for (const off of [-w / 2 + 0.03, w / 2 - 0.03]) {
      const s = shoulder.clone(); s.translate(off, 0, 0); tyreParts.push(nonIndexed(s));
    }
    shoulder.dispose();
    /**
     * Sidewall relief in place of lettering. A raised bead ring plus a serrated decorative
     * band is what actually reads at 300 % — real white lettering would need a texture, and
     * the tyre is an InstancedMesh sharing `look`'s `truck.tyre` key, so there is nowhere to
     * put one without a private clone (§4.9e). Both features sit at r 0.36…0.47, i.e. INSIDE
     * fx's opaque annulus, so the serrations are covered by the blur exactly like the spokes.
     */
    for (const s of [-1, 1]) {
      const bead = new THREE.TorusGeometry(r * T.beadRadius, T.beadThick, 4, 16);
      bead.rotateY(Math.PI / 2);
      bead.translate(s * (w / 2 - T.beadInset), 0, 0);
      tyreParts.push(nonIndexed(bead));
      for (let i = 0; i < T.serrationCount; i++) {
        const a = (i / T.serrationCount) * Math.PI * 2;
        const n = new THREE.BoxGeometry(T.serrationDepth, r * T.serrationLength, 0.028);
        n.translate(0, r * T.serrationRadius, 0);
        n.rotateX(a);
        n.translate(s * (w / 2 - T.serrationDepth * 0.5), 0, 0);
        tyreParts.push(nonIndexed(n));
      }
    }
    // Sidewalls: a ring whose bore is narrower than the rim disc behind it, so nothing can
    // be seen through the wheel. Each face is turned to point outboard.
    for (const s of [-1, 1]) {
      const wall = new THREE.RingGeometry(r * WF.sidewallBore, r, 18, 1);
      wall.rotateY(s * Math.PI / 2);
      wall.translate(s * w * 0.5, 0, 0);
      tyreParts.push(nonIndexed(wall));
    }
    const tyre = mergeGeometries(tyreParts, false);
    for (const g of tyreParts) g.dispose();

    /**
     * ROUND 4, item 2 — MOTION. The wheel is the only part of the truck that MUST be moving,
     * and a smooth disc cannot show that it is: a flat, uniformly-shaded circular face looks
     * identical at every angle, so the old rim (a 12-sided disc plus six 0.07 m bars sunk
     * almost flush) read as a static grey coin at 69 km/h.
     *
     * ROUND 7 CORRECTION — the round-4 rim stood PROUD of the tyre wall to give `fx`'s
     * streak something to smear, and that is exactly what defeated it. See TUNING.wheelFace:
     * fx pins its blur disc `faceOffset` = 0.19 m off the wheel centre, and the round-4 hub
     * (±0.234), spokes (±0.230) and lugs (±0.245) all stood IN FRONT of that plane, so the
     * one sharp thing on the wheel was drawn over the blur. Measured on `street-speed`:
     * at 400 % zoom the rear hub spokes are individually legible at a claimed 87 km/h.
     *
     * The features stay — they are what makes rotation readable when the truck is slow —
     * but every one of them is now inside the blur plane, and the spoke star is pushed out
     * radially into the sprite's opaque annulus (fx paints r = 0.26..0.86 solid and leaves
     * the hub cap clear on purpose) instead of sitting in the clear middle.
     */
    const rimGeo = new THREE.CylinderGeometry(r * WF.rimRadius, r * WF.rimRadius,
      w * WF.rimWidth, WF.rimSegments, 1);
    rimGeo.rotateZ(Math.PI / 2);
    // Hub cap. Lives inside fx's clear bore, so it is deliberately SMOOTH and rotationally
    // symmetric: 20 sides, no angular detail, nothing that can look different at a different
    // spin angle. This is the only thing the blur sprite leaves sharp, on purpose.
    const hub = new THREE.CylinderGeometry(r * WF.hubRadius, r * WF.hubRadius * 0.86,
      w * WF.hubWidth, WF.hubSegments);
    hub.rotateZ(Math.PI / 2);
    const rimParts = [nonIndexed(rimGeo), nonIndexed(hub)];
    // Five spokes, not six: an odd count never presents a symmetric pattern, so no rotation
    // angle ever looks like the rest pose. Pushed radially OUT past `clearBore` in round 9 —
    // see TUNING.wheelFace: their inner tips used to sit in the one window fx's sprite
    // guarantees it will not cover, which is what kept the wheel legible at 87 km/h.
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2;
      // Translate FIRST, then rotate about the axle: the spoke's long axis has to end up
      // radial. Rotating a pre-placed box about the origin just slides it back to angle 0.
      const n = new THREE.BoxGeometry(w * WF.spokeWidth, r * WF.spokeLength, 0.10);
      n.translate(0, r * WF.spokeCentre, 0);
      n.rotateX(a);
      rimParts.push(nonIndexed(n));
    }
    // Lug ring: the highest-frequency detail on the wheel, and therefore the feature that
    // most needs to be UNDER the blur. Moved out to r * lugRadius so it is.
    for (let i = 0; i < WF.lugCount; i++) {
      const a = (i / WF.lugCount) * Math.PI * 2 + 0.62;
      const b = new THREE.CylinderGeometry(WF.lugBoltRadius, WF.lugBoltRadius, w * WF.lugWidth, 5);
      b.rotateZ(Math.PI / 2);
      b.translate(0, Math.cos(a) * r * WF.lugRadius, Math.sin(a) * r * WF.lugRadius);
      rimParts.push(nonIndexed(b));
    }
    const rim = mergeGeometries(rimParts, false);
    for (const g of rimParts) g.dispose();

    this.tyreMesh = new THREE.InstancedMesh(tyre, this._shared('truck.tyre', 'truck.metal'), this.wheelCount);
    // Cached tint VARIANT, not a clone: shares the base shader program (zero of our 45) and
    // still receives every `applyLook` weather/night update (§4.9e). See TUNING.wheelFace.
    const rimMat = this.ctx.materials.get(this._key('truck.metal'), { tint: WF.rimTint });
    this.rimMesh = new THREE.InstancedMesh(rim, rimMat, this.wheelCount);
    for (const m of [this.tyreMesh, this.rimMesh]) {
      m.castShadow = true;
      m.receiveShadow = false;
      m.frustumCulled = false;
      m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      this.root.add(m);
    }
    this._geometries.push(tyre, rim);
    this.tyreMesh.name = 'truck.tyres';
    this.rimMesh.name = 'truck.rims';
    for (let i = 0; i < this.wheelCount; i++) this.setWheel(i, 0, 0, 0);
    this.commitWheels();
  }

  /** Amber rotating beacon bar on the cab roof. */
  _buildBeacon() {
    this.beacon = new THREE.Group();
    this.beacon.position.set(0, 3.08, 1.20);
    this.body.add(this.beacon);

    const parts = new PartList();
    parts.add(box(0.98, 0.16, 0.20), { y: 0.10 });
    parts.add(box(0.24, 0.22, 0.24), { x: 0.34, y: 0.10 });
    parts.add(box(0.24, 0.22, 0.24), { x: -0.34, y: 0.10 });
    this.beaconMesh = this._mesh(parts.merge('truck.beacon', 1), this.beaconMaterial, 'truck.beacon', this.beacon);
    if (this.beaconMesh) this.beaconMesh.castShadow = false;

    const base = new PartList();
    base.add(box(1.06, 0.08, 0.26), { y: 3.02, z: 1.20 });
    this._mesh(base.merge('truck.beaconBase', 1), this._shared('truck.chassis', 'truck.metal'), 'truck.beaconBase');
  }

  /**
   * The repair boom. Gameplay aims it (ctx.player.aimNozzle); we own the geometry and
   * publish the world-space tip + direction every fixed step. See TUNING.dims for the
   * round-9 diagnosis of why this stopped being a straight pipe.
   *
   * Scene-graph chain, so `computeNozzleLocal` and the meshes can never disagree:
   *
   *   body → nozzlePivot   (rotation.y = yaw)      slew turret, ram mast, ram pair
   *          ├─ nozzleRam  (rotation.x = derived)  barrel  ─┐ driven from boom pitch
   *          │   └─ rod    (scale.z  = derived)    rod     ─┘ in _applyRam()
   *          └─ nozzleBoom (rotation.x = pitch)    outer girder + ram clevis
   *              └─ stinger(position.z = -extend)  telescopic inner section
   *                  └─ wrist (rotation.x = wrist) elbow, lance, spray head
   */
  _buildNozzle() {
    const P = D.nozzlePivot;
    // Painted steel for the arm structure; chrome (untinted `truck.metal`) for the rams.
    // See TUNING.dims.boomTint for why the arm cannot be chrome.
    const metal = this.ctx.materials.get(this._key('truck.metal'), { tint: D.boomTint });
    const chrome = this._shared('truck.metal');
    const bl = D.nozzleBoomLength;
    const A = D.ramAnchor, CV = D.ramClevis;

    // ---- fixed pedestal, in body space: this is the part that does NOT slew.
    const base = new PartList();
    // Sits ON the hopper's rear rim rail (TUNING.hopper.rimY 2.62), not floating above the
    // load: the pivot is at y 2.80, so the ring's underside lands at 2.52 and the mounting
    // plate spans the rail. Hardcoding a height here instead of deriving it from the rim is
    // the same class of bug as §4.9c.
    base.add(new THREE.CylinderGeometry(0.26, 0.30, 0.16, 12), { x: P.x, y: P.y - 0.20, z: P.z });
    base.add(box(0.64, 0.09, 0.54), { x: P.x, y: TUNING.hopper.rimY + 0.05, z: P.z });
    this._mesh(base.merge('truck.nozzleBase', 1),
      this._shared('truck.chassis', 'truck.metal'), 'truck.nozzleBase');

    this.nozzlePivot = new THREE.Group();
    this.nozzlePivot.position.set(P.x, P.y, P.z);
    this.body.add(this.nozzlePivot);

    this.nozzleBoom = new THREE.Group();
    this.nozzlePivot.add(this.nozzleBoom);

    // ---- turret: slew ring, housing, shoulder bearing cheeks, and the ram mast.
    const turret = new PartList();
    turret.add(new THREE.CylinderGeometry(0.24, 0.24, 0.10, 14), { y: -0.13 });
    turret.add(box(0.40, 0.26, 0.52), { y: 0.02, z: 0.06 });
    for (const sx of [1, -1]) {
      turret.add(box(0.07, 0.30, 0.30), { x: sx * 0.16, y: 0.01, z: -0.04 });
      // Ram mast — the A-frame the rams push off.
      turret.add(box(0.08, 0.46, 0.10), { x: sx * D.ramSideOffset, y: 0.24, z: A.z, rx: -0.16 });
      turret.add(new THREE.CylinderGeometry(0.05, 0.05, 0.10, 8),
        { x: sx * D.ramSideOffset, y: A.y, z: A.z, rz: Math.PI / 2 });
    }
    turret.add(new THREE.CylinderGeometry(0.10, 0.10, 0.42, 10), { rz: Math.PI / 2 });
    this._mesh(turret.merge('truck.nozzleTurret', 1), metal, 'truck.nozzleTurret', this.nozzlePivot);

    // ---- outer girder: a box beam with a top chord and three lattice diagonals, so the
    // arm has internal structure at the one scale the close-up actually resolves.
    const boom = new PartList();
    boom.add(box(0.19, 0.22, bl), { z: -bl / 2 });
    boom.add(new THREE.CylinderGeometry(0.045, 0.045, bl * 0.94, 8), { y: 0.20, z: -bl / 2, rx: Math.PI / 2 });
    for (let i = 0; i < 3; i++) {
      const t = (i + 0.5) / 3;
      boom.add(box(0.05, 0.25, 0.05), { y: 0.10, z: -bl * t, rx: (i % 2 ? 1 : -1) * 0.72 });
    }
    // Ram clevis: the pair of ears the rods pin into.
    for (const sx of [1, -1]) {
      boom.add(box(0.05, 0.16, 0.14), { x: sx * D.ramSideOffset, y: CV.y - 0.03, z: CV.z });
    }
    boom.add(box(0.30, 0.06, 0.14), { y: CV.y - 0.06, z: CV.z });
    // Hose run along the top of the girder — the detail that says "this thing carries mix".
    boom.add(new THREE.CylinderGeometry(0.035, 0.035, bl * 0.8, 6),
      { x: 0.11, y: 0.13, z: -bl * 0.52, rx: Math.PI / 2 });
    this._mesh(boom.merge('truck.nozzleBoom', 1), metal, 'truck.nozzleBoom', this.nozzleBoom);

    // ---- telescopic stinger. Slides out of the girder by `extend`.
    this.nozzleStinger = new THREE.Group();
    this.nozzleBoom.add(this.nozzleStinger);
    const sting = new PartList();
    sting.add(box(0.13, 0.155, bl), { z: -bl / 2 });
    sting.add(box(0.17, 0.19, 0.07), { z: -bl + 0.05 });      // wear pad / stop collar
    this._mesh(sting.merge('truck.nozzleStinger', 1),
      this._shared('truck.chassis', 'truck.metal'), 'truck.nozzleStinger', this.nozzleStinger);

    // ---- wrist: elbow, lance, spray head. Hangs off the stinger's far end.
    this.nozzleWrist = new THREE.Group();
    this.nozzleWrist.position.z = -bl;
    this.nozzleStinger.add(this.nozzleWrist);

    const wrist = new PartList();
    const ll = D.nozzleLanceLength, hr = D.nozzleHeadReach;
    wrist.add(new THREE.CylinderGeometry(0.095, 0.095, 0.34, 10), { rz: Math.PI / 2 });
    for (const sx of [1, -1]) wrist.add(box(0.05, 0.20, 0.20), { x: sx * 0.12, y: -0.02 });
    wrist.add(new THREE.CylinderGeometry(0.062, 0.075, ll, 10), { z: -ll / 2, rx: Math.PI / 2 });
    // Feed hose looping from the elbow down the back of the lance.
    wrist.add(new THREE.CylinderGeometry(0.030, 0.030, ll * 0.82, 6), { y: 0.10, z: -ll * 0.5, rx: Math.PI / 2 });
    // Spray head: a flared shroud with the mouth facing -Z, two skid shoes and a hood rib.
    wrist.add(taper(0.44, 0.28, hr, 0.34), { z: -ll - hr / 2, rx: Math.PI / 2 });
    wrist.add(box(0.38, 0.05, 0.10), { y: 0.13, z: -ll - hr * 0.45 });
    for (const sx of [1, -1]) {
      wrist.add(box(0.05, 0.13, 0.16), { x: sx * 0.20, y: -0.10, z: -ll - hr * 0.85 });
    }
    this._mesh(wrist.merge('truck.nozzleHead', 1), metal, 'truck.nozzleHead', this.nozzleWrist);

    // ---- the ram pair. Barrel rotates about the mast pin; the rod scales along its axis.
    this.nozzleRam = new THREE.Group();
    this.nozzleRam.position.set(0, A.y, A.z);
    this.nozzlePivot.add(this.nozzleRam);

    const barrel = new PartList();
    for (const sx of [1, -1]) {
      barrel.add(new THREE.CylinderGeometry(D.ramBarrelRadius, D.ramBarrelRadius, D.ramBarrelLength, 10),
        { x: sx * D.ramSideOffset, z: -D.ramBarrelLength / 2, rx: Math.PI / 2 });
      barrel.add(new THREE.CylinderGeometry(D.ramBarrelRadius * 1.2, D.ramBarrelRadius * 1.2, 0.06, 10),
        { x: sx * D.ramSideOffset, z: -D.ramBarrelLength + 0.02, rx: Math.PI / 2 });
    }
    this._mesh(barrel.merge('truck.nozzleRam', 1), chrome, 'truck.nozzleRam', this.nozzleRam);

    // Rod: authored as a UNIT length running 0 → -1 so `scale.z` is metres of exposed rod.
    // A cylinder scaled along its own axis stays a cylinder, so this never shears.
    this.nozzleRod = new THREE.Group();
    this.nozzleRod.position.z = -D.ramBarrelLength * 0.88;
    this.nozzleRam.add(this.nozzleRod);
    const rod = new PartList();
    for (const sx of [1, -1]) {
      const g = new THREE.CylinderGeometry(D.ramRodRadius, D.ramRodRadius, 1, 8);
      g.rotateX(Math.PI / 2);
      g.translate(sx * D.ramSideOffset, 0, -0.5);
      rod.add(g, null);
    }
    this._mesh(rod.merge('truck.nozzleRod', 1), chrome, 'truck.nozzleRod', this.nozzleRod);

    this.setNozzle(0, 0, 0, 0);
  }

  /**
   * Point the ram pair at the girder's clevis and set the exposed rod length. Called from
   * `setNozzle`; pure trig on scratch scalars, no allocation (N9).
   */
  _applyRam(pitch) {
    const A = D.ramAnchor, CV = D.ramClevis;
    // Clevis position in PIVOT space: the boom has rotated it about X by `pitch`.
    const c = Math.cos(pitch), s = Math.sin(pitch);
    const by = CV.y * c - CV.z * s;
    const bz = CV.y * s + CV.z * c;
    const dy = by - A.y, dz = bz - A.z;
    const len = Math.sqrt(dy * dy + dz * dz);
    // The ram's own axis is local -Z, and Rx(t) maps (0,0,-1) → (0, sin t, -cos t).
    this.nozzleRam.rotation.x = Math.atan2(dy, -dz);
    const exposed = len - D.ramBarrelLength * 0.88;
    this.nozzleRod.scale.z = exposed > 0.02 ? exposed : 0.02;
  }

  // ------------------------------------------------------------------ per-frame

  /**
   * ROUND 4 — item 1, grounding. `groundDy` replaces the old compression term.
   *
   * The wheels are instanced on `root`, whose origin springs vertically around the mean of the
   * four wheel-plane samples. The old term was `drop = (0.5 - compression) * travel`, which
   * moved the WHEEL down when the suspension compressed — backwards: a compressed suspension
   * moves the BODY down and leaves the wheel exactly where the road is. Combined with the
   * spring on `root`, every wheel floated or sank together by up to ±0.10 m, which is 18 % of
   * a tyre radius of daylight under the truck in a frame the critic reads for grounding.
   *
   * `groundDy` is the ground height at THIS wheel minus the chassis origin height, already
   * clamped by the caller to the visible travel, so the tyre now sits on its own patch of road
   * on a crown, a camber or a kerb, and still droops visibly in the air.
   *
   * @param {number} i          visual wheel index
   * @param {number} spin       rotation about the axle, radians
   * @param {number} steer      steer angle (front wheels only), radians
   * @param {number} groundDy   ground height under this wheel, relative to the chassis origin
   */
  setWheel(i, spin, steer, groundDy) {
    const m = this.wheelMounts[i];
    const drop = (groundDy || 0) - TUNING.wheelGround.bite;
    this._wheelPos.set(m[0], m[1] + drop, m[2]);
    this._wheelEuler.set(spin, steer, 0, 'YXZ');
    this._wheelQuat.setFromEuler(this._wheelEuler);
    this._wheelMatrix.compose(this._wheelPos, this._wheelQuat, this._wheelScale);
    this.tyreMesh.setMatrixAt(i, this._wheelMatrix);
    this.rimMesh.setMatrixAt(i, this._wheelMatrix);
  }

  commitWheels() {
    this.tyreMesh.instanceMatrix.needsUpdate = true;
    this.rimMesh.instanceMatrix.needsUpdate = true;
  }

  /** Beacon spin + pulse. Pure function of sim time so shots stay bit-identical. */
  setBeacon(rotation, gain) {
    this.beacon.rotation.y = rotation;
    TruckModel.setLampGain(this.beaconMaterial, gain);
  }

  setTailLights(gain) { TruckModel.setLampGain(this.tailMaterial, gain); }

  /**
   * Hopper load level, 0..1. The heap geometry is authored 1 m tall with its pivot on the bin
   * floor, so scaling y and compensating the origin offset grows it from the floor upward.
   * Presentation only — driven from ctx.gameplay counts, which are simulation state, so shots
   * stay bit-identical.
   */
  setLoad(fill01) {
    if (!this.loadMesh) return;
    const HP = TUNING.hopper;
    const f = fill01 < 0 ? 0 : fill01 > 1 ? 1 : fill01;
    const h = HP.loadHeightMin + (HP.loadHeightMax - HP.loadHeightMin) * f;
    this.loadMesh.scale.y = h;
    this.loadMesh.position.y = this.loadBaseY * (1 - h);
  }

  /**
   * Aim the boom. `yaw` slews the turret about the truck's up axis, `pitch` lifts the girder
   * about its right axis (+ = up), `extend` runs the stinger out, `wrist` cranks the elbow.
   */
  setNozzle(yaw, pitch, extend, wrist) {
    this.nozzlePivot.rotation.y = yaw;
    this.nozzleBoom.rotation.x = pitch;
    this.nozzleStinger.position.z = -extend;
    this.nozzleWrist.rotation.x = wrist || 0;
    this._applyRam(pitch);
  }

  /**
   * Spray-head mouth in TRUCK-LOCAL space, computed analytically so simulation code never has
   * to touch the scene graph (which is only current during the presentation pass).
   *
   * Must stay the exact algebra of the group chain in `_buildNozzle`:
   *   tip = P + Ry(yaw) · [ Rx(pitch)·(0,0,-(boom+extend)) + Rx(pitch+wrist)·(0,0,-lance-head) ]
   * Every point on that chain has local x = 0, so the yaw step reduces to
   * (x, z) = (zTotal·sin ψ, zTotal·cos ψ).
   *
   * Returns `out`; also writes the unit aim direction into `dirOut` if supplied.
   */
  computeNozzleLocal(out, yaw, pitch, extend, wrist, dirOut) {
    const P = D.nozzlePivot;
    const l1 = D.nozzleBoomLength + extend;
    const l2 = D.nozzleLanceLength + D.nozzleHeadReach;
    const w = pitch + (wrist || 0);
    const cp = Math.cos(pitch), sp = Math.sin(pitch);
    const cw = Math.cos(w), sw = Math.sin(w);
    const yTotal = l1 * sp + l2 * sw;
    const zTotal = -(l1 * cp) - (l2 * cw);
    const cy = Math.cos(yaw), sy = Math.sin(yaw);
    out.set(P.x + zTotal * sy, P.y + yTotal, P.z + zTotal * cy);
    if (dirOut) {
      // The jet leaves along the WRIST's axis, not the girder's.
      dirOut.set(-cw * sy, sw, -cw * cy).normalize();
    }
    return out;
  }

  dispose() {
    for (const g of this._geometries) g.dispose();
    for (const m of this._materials) m.dispose();
    for (const t of this._textures) t.dispose();
    this._geometries.length = 0;
    this._materials.length = 0;
    this._textures.length = 0;
    if (this.root.parent) this.root.parent.remove(this.root);
  }
}
